import { useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useProfile } from "@/hooks/useProfile";
import { useSubscription } from "@/hooks/useSubscription";
import { usePostHog } from "@/contexts/PostHogProvider";

// This component bridges Auth/Profile/Subscription data into PostHog
// It performs side-effects only and renders nothing
const AnalyticsIdentityBridge = () => {
  const { isInitialized, posthog } = usePostHog();
  const { user } = useAuth();
  const { profile } = useProfile();
  const { subscribed, trial_active, subscription_tier, loading: subscriptionLoading } = useSubscription();

  // Identify the user and set properties
  useEffect(() => {
    if (!isInitialized || !posthog || !user) return;

    try {
      posthog.identify(user.id, {
        email: user.email,
        created_at: user.created_at,
      });

      if (profile) {
        posthog.setPersonProperties({
          display_name: profile.display_name,
          christian_tradition: profile.christian_tradition,
          onboarding_completed: profile.onboarding_completed,
          words_target: profile.words_target,
          invitation_code_used: profile.invitation_code_used,
        });
      }

      if (!subscriptionLoading) {
        posthog.setPersonProperties({
          has_subscription: subscribed,
          trial_active: trial_active,
          subscription_status: subscribed ? "active" : "none",
          subscription_tier: subscription_tier || "none",
        });
      }
    } catch (err) {
      console.warn("PostHog identify failed:", err);
    }
  }, [isInitialized, posthog, user, profile, subscribed, trial_active, subscription_tier, subscriptionLoading]);

  // Reset on logout
  useEffect(() => {
    if (!isInitialized || !posthog) return;
    if (!user) {
      try { posthog.reset(); } catch {
        // PostHog reset can throw if the session was never initialised; ignore.
      }
    }
  }, [isInitialized, posthog, user]);

  return null;
};

export default AnalyticsIdentityBridge;
